import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {StyleSheet, useWindowDimensions, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {Avatar, CheckBox, Icon, ListItem, makeStyles} from '@rneui/themed';

import {AcceptOrderFormData, acceptOrderSchema} from '../../../schemas/checkoutSchemas';
import {useStores} from '../../../stores/use-stores';
import {cleanPhoneNumber} from '../../../utils/phoneNumber';
import Over18 from '../../Over18';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import TextInput from '../../themed/TextInput';
import CrossPlatformModal from '../../ui-controls/CrossPlatformModal';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

function InputLabel({children, required}: {children: React.ReactNode; required?: boolean}) {
  return (
    <Text variant="body2" style={styles.inputLabel}>
      {children}
      {!!required && (
        <Text variant="caption" style={styles.requiredAsterisk} accessibilityLabel="required field">
          {' *'}
        </Text>
      )}
    </Text>
  );
}

function PersonAcceptingTheOrder() {
  const listStyles = useListStyles();
  const [isVisible, setIsVisible] = React.useState(false);
  const {cartStore, userStore} = useStores();
  const {width: screenWidth, height: screenHeight} = useWindowDimensions();
  const modalWidth = Math.min(screenWidth * 0.9, 480);
  const modalMaxHeight = screenHeight * 0.9;

  // Set willBePresent to true on mount if no person is nominated
  useEffect(() => {
    if (!cartStore.willBePresent && !cartStore.nominatedPersonName) {
      cartStore.setWillBePresent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: {isSubmitting, isSubmitted},
  } = useForm<AcceptOrderFormData>({
    mode: 'onTouched',
    resolver: zodResolver(acceptOrderSchema),
    defaultValues: {
      firstName: cartStore.nominatedPersonParts?.firstName || '',
      lastName: cartStore.nominatedPersonParts?.lastName || '',
      mobile: cartStore.nominatedPersonParts?.mobile || '',
      person: cartStore.hasNominatedPerson ? 'someone_else' : 'me',
    },
  });

  const personValue = watch('person');

  const onClose = () => {
    setIsVisible(false);
  };

  const onOpen = () => {
    setIsVisible(true);
  };

  // Reset form when modal opens - only trigger on isVisible change
  useEffect(() => {
    if (isVisible) {
      reset({
        firstName: cartStore.nominatedPersonParts?.firstName || '',
        lastName: cartStore.nominatedPersonParts?.lastName || '',
        mobile: cartStore.nominatedPersonParts?.mobile || '',
        person: cartStore.hasNominatedPerson ? 'someone_else' : 'me',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const onSubmit = (data: AcceptOrderFormData) => {
    if (data.person === 'someone_else') {
      // Clean phone number before saving (convert 0 prefix to +61)
      const cleanedMobile = data.mobile ? cleanPhoneNumber(data.mobile) : '';
      cartStore.setNominatedPersonParts({
        firstName: data.firstName?.trim() || '',
        lastName: data.lastName?.trim() || '',
        mobile: cleanedMobile,
      });
      cartStore.setWillBePresent(false);
      cartStore.setHasNominatedPerson(true);
    } else if (data.person === 'me') {
      cartStore.setNominatedPersonParts({
        firstName: '',
        lastName: '',
        mobile: '',
      });
      cartStore.setWillBePresent(true);
      cartStore.setHasNominatedPerson(false);
    }

    onClose();
  };

  const displayName = cartStore.hasNominatedPerson
    ? cartStore.nominatedPersonName
        .split('\t')
        .filter((_, i) => i < 2)
        .join(' ')
    : userStore.name;

  return (
    <>
      <ListItem onPress={onOpen}>
        <Avatar
          icon={{
            type: 'material',
            name: 'person-outline',
            size: 24,
            color: 'black',
          }}
        />
        <ListItem.Content>
          <ListItem.Subtitle>Person accepting the order</ListItem.Subtitle>
          <ListItem.Title>
            <Text variant="body2">{displayName}</Text>
          </ListItem.Title>
        </ListItem.Content>
        <ListItem.Chevron size={24} color={listStyles.text.color} />
      </ListItem>

      <CrossPlatformModal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        avoidKeyboard
        keyboardAwareContent
        style={styles.modal}>
        <View style={[styles.modalContent, {width: modalWidth, maxHeight: modalMaxHeight}]}>
          {/* Header with title and close icon */}
          <View style={styles.header}>
            <Text variant="heading1">Who will accept the order?</Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>

          {/* Radio selection */}
          <Controller
            control={control}
            name="person"
            render={({field: {onChange, value}}) => (
              <View style={styles.radioGroup}>
                <PressableWithOpacityFeedback
                  onPress={() => onChange('me')}
                  style={styles.radioOption}>
                  <CheckBox
                    checked={value === 'me'}
                    onPress={() => onChange('me')}
                    iconType="material-design"
                    checkedIcon="radiobox-marked"
                    uncheckedIcon="radiobox-blank"
                    title="Me"
                    textStyle={styles.radioText}
                    containerStyle={styles.radioContainer}
                  />
                </PressableWithOpacityFeedback>
                <PressableWithOpacityFeedback
                  onPress={() => onChange('someone_else')}
                  style={styles.radioOption}>
                  <CheckBox
                    checked={value === 'someone_else'}
                    onPress={() => onChange('someone_else')}
                    iconType="material-design"
                    checkedIcon="radiobox-marked"
                    uncheckedIcon="radiobox-blank"
                    title="Someone else"
                    textStyle={styles.radioText}
                    containerStyle={styles.radioContainer}
                  />
                </PressableWithOpacityFeedback>
              </View>
            )}
          />

          {/* Person details */}
          {personValue === 'me' ? (
            <View style={styles.readOnlySection}>
              <View style={styles.readOnlyField}>
                <Text style={styles.fieldLabel}>First name</Text>
                <Text style={styles.fieldValue}>{userStore.firstName}</Text>
              </View>
              <View style={styles.readOnlyField}>
                <Text style={styles.fieldLabel}>Last name</Text>
                <Text style={styles.fieldValue}>{userStore.lastName}</Text>
              </View>
              <View style={styles.readOnlyField}>
                <Text style={styles.fieldLabel}>Mobile number</Text>
                <Text style={styles.fieldValue}>{userStore.phoneNumber}</Text>
              </View>
            </View>
          ) : (
            <View style={styles.formSection}>
              <Controller
                control={control}
                name="firstName"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    label={<InputLabel required>First name</InputLabel>}
                    value={value || ''}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    autoCapitalize="words"
                    autoComplete="given-name"
                    touched={isTouched || isSubmitted}
                    errorMessageWhenTouched={error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="lastName"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    label={<InputLabel required>Last name</InputLabel>}
                    value={value || ''}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    autoCapitalize="words"
                    autoComplete="family-name"
                    touched={isTouched || isSubmitted}
                    errorMessageWhenTouched={error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="mobile"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    label={<InputLabel required>Mobile number</InputLabel>}
                    value={value || ''}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType="phone-pad"
                    autoComplete="tel"
                    placeholder="e.g. 0412 345 678"
                    touched={isTouched || isSubmitted}
                    errorMessageWhenTouched={error?.message}
                  />
                )}
              />
            </View>
          )}

          {/* Save button */}
          <View style={styles.buttonsContainer}>
            <Button loading={isSubmitting} disabled={isSubmitting} onPress={handleSubmit(onSubmit)}>
              SAVE
            </Button>
          </View>

          <Over18 />
        </View>
      </CrossPlatformModal>
    </>
  );
}

const styles = StyleSheet.create({
  inputLabel: {
    marginBottom: 5,
  },
  requiredAsterisk: {
    fontSize: 14,
    // TODO: added to theme token after centralized theming implementation
    color: '#ef4444',
    marginLeft: 2,
    lineHeight: 14,
  },
  modal: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  modalContent: {
    minWidth: 320,
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  radioGroup: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  radioOption: {
    flex: 1,
  },
  radioText: {
    fontWeight: 'normal',
  },
  radioContainer: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    padding: 0,
    marginLeft: 0,
  },
  readOnlySection: {
    gap: 12,
    paddingVertical: 8,
  },
  readOnlyField: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    // TODO: added to theme token after centralized theming implementation
    borderBottomColor: '#eee',
  },
  fieldLabel: {
    fontSize: 12,
    // TODO: added to theme token after centralized theming implementation
    color: '#666',
    marginBottom: 4,
  },
  fieldValue: {
    fontSize: 16,
    // TODO: added to theme token after centralized theming implementation
    color: '#000',
  },
  formSection: {
    gap: 12,
  },
  buttonsContainer: {
    marginTop: 24,
    marginBottom: 16,
  },
});

const useListStyles = makeStyles(theme => {
  return {
    text: {
      // TODO: added to theme token after centralized theming implementation
      color: theme.colors?.black ?? '#000',
    },
  };
});

export default PersonAcceptingTheOrder;
